import {
  Container,
  Input,
  Label,
  StyledGroupButton,
} from './ButtonGroup.styles';

interface Props<OptionType extends string> {
  options: OptionType[];
  selectedOption?: OptionType;
  setSelectedOption: (option: OptionType) => void;
  size?: 'small' | 'medium' | 'large';
  buttonWidth?: string;
  fullWidth?: boolean;
  'data-testid'?: string;
  buttonGroupOptionTestId?: string;
  disabled?: boolean;
  disabledOptions?: string[];
  id?: string;
  tabIndex?: number;
}

export const ButtonGroup = <OptionType extends string>({
  options,
  size = 'medium',
  selectedOption,
  setSelectedOption,
  buttonWidth,
  fullWidth,
  buttonGroupOptionTestId,
  disabled,
  disabledOptions,
  id,
  tabIndex,
  ...props
}: Props<OptionType>) => {
  const isGroupButtonEnabled = (option: OptionType) => {
    return !disabled && !disabledOptions?.includes(option);
  };

  return (
    <Container
      data-testid={props['data-testid']}
      $fullWidth={fullWidth}
      {...props}
    >
      {options.map((option, index) => (
        <StyledGroupButton
          size={size}
          width={buttonWidth}
          $fullWidth={fullWidth}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isGroupButtonEnabled(option)) {
              setSelectedOption(option);
            }
          }}
          previous={
            !!selectedOption && index === options.indexOf(selectedOption) - 1
          }
          selected={selectedOption === option}
          data-testid={`${buttonGroupOptionTestId}-${index + 1}`}
          key={id ? `${id}-${option}` : option}
          disabled={!isGroupButtonEnabled(option)}
        >
          <Input
            type="radio"
            id={id ? `${id}-${option}` : option}
            value={option}
            checked={selectedOption === option}
            disabled={!isGroupButtonEnabled(option)}
            readOnly
            tabIndex={tabIndex}
          />
          <Label htmlFor={option} disabled={!isGroupButtonEnabled(option)}>
            {option}
          </Label>
        </StyledGroupButton>
      ))}
    </Container>
  );
};
